import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { RootState } from '../types/graph';
import { useMode } from '../hooks/useMode';
import { SHARED_ROUTES } from '../routes';

// import { usePostHog } from 'posthog-js/react';
import { StepId } from '../onboarding/orchestration/types';
import {
  computeFlow,
  getBaseFlow,
  routeToStepId,
  stepToRoute,
  isOffboardingStep,
  resolveBaseStepId,
} from '../onboarding/orchestration/computeFlow';
import { buildOnboardingContext } from '../onboarding/orchestration/buildContext';
import {
  selectOnboardingFlow,
  selectCurrentStepIndex,
} from '../onboarding/orchestration/selectors';
import {
  setOnboardingFlow,
  setCurrentStepIndex,
  setAssistedByPro,
  // setExperimentFlags,
} from '../reducers/graph';
import { filterOnboardingQuestions } from '../util/questionnaire';

interface QuestionnaireNavigationContextType {
  navigateNext: () => void;
  navigatePrevious: () => void;
  navigateToEditPage: () => void;
  currentPage: string;
  showErrors: boolean;
  setShowErrors: (show: boolean) => void;
  isQuestionExiting: boolean;
  setIsQuestionExiting: (exiting: boolean) => void;
  hasFreshSelection: boolean;
  setHasFreshSelection: (hasFresh: boolean) => void;
  mandatoryQuestionsWarning: boolean;
  setMandatoryQuestionsWarning: (warning: boolean) => void;
  navigationDirection: 'forward' | 'backward';
  setNavigationDirection: (direction: 'forward' | 'backward') => void;
  /** Ref to check if navigation is currently in progress (synchronous check) */
  isNavigatingRef: React.MutableRefObject<boolean>;
}

const defaultIsNavigatingRef = { current: false };

const QuestionnaireNavigationContext =
  createContext<QuestionnaireNavigationContextType>({
    navigateNext: () => null,
    navigatePrevious: () => null,
    navigateToEditPage: () => null,
    currentPage: '',
    showErrors: false,
    setShowErrors: () => null,
    isQuestionExiting: false,
    setIsQuestionExiting: () => null,
    hasFreshSelection: false,
    setHasFreshSelection: () => null,
    mandatoryQuestionsWarning: false,
    setMandatoryQuestionsWarning: () => null,
    navigationDirection: 'forward',
    setNavigationDirection: () => null,
    isNavigatingRef: defaultIsNavigatingRef,
  });

interface NavigationProviderProps {
  children: ReactNode;
  onSubmit?: () => void;
}

export const NavigationProvider = ({ children }: NavigationProviderProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [urlSearchParams] = useSearchParams();
  // In-office assisted launch carries `assisted=true` in the URL so the signal
  // survives store resets (PURGE / redux-persist rehydrate) that would
  // otherwise revert the ASSISTED_BY_PRO Redux flag before flow[0] is computed.
  const assistedFromUrl = urlSearchParams.get('assisted') === 'true';
  const store = useStore<RootState>();
  const currentPage = useSelector(
    (state: RootState) => state.graph.currentPage,
  );
  const mode = useMode();
  const [showErrors, setShowErrors] = useState(false);
  const [isQuestionExiting, setIsQuestionExiting] = useState(false);
  const [hasFreshSelection, setHasFreshSelection] = useState(false);
  const [mandatoryQuestionsWarning, setMandatoryQuestionsWarning] =
    useState(false);
  const [navigationDirection, setNavigationDirection] = useState<
    'forward' | 'backward'
  >('forward');
  const isNavigatingRef = useRef(false);
  const prevPathRef = useRef(location.pathname);
  const questionnaireConfig = useSelector(
    (state: RootState) => state.questionnaire.config,
  );

  const [pages, setPages] = useState<Array<string>>([]);
  const reduxState = useSelector((state: RootState) => state);
  const flow = useSelector(selectOnboardingFlow);
  const currentStepIndex = useSelector(selectCurrentStepIndex);
  const assistedByPro = reduxState.graph.options?.ASSISTED_BY_PRO ?? false;

  // Re-establish the assisted-by-pro flag from the durable URL signal. Runs
  // after any store reset (PURGE / rehydrate) that cleared graph.options, so
  // the flow is computed with the skip applied for in-office launches.
  useEffect(() => {
    if (assistedFromUrl && !assistedByPro) {
      dispatch(setAssistedByPro(true));
    }
  }, [assistedFromUrl, assistedByPro, dispatch]);

  useEffect(() => {
    if (questionnaireConfig) {
      setPages(Object.keys(questionnaireConfig.pages));
    }
  }, [questionnaireConfig]);

  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      prevPathRef.current = location.pathname;
      isNavigatingRef.current = false;
    }
  }, [location.pathname]);

  const onboardingContext = useMemo(
    () => buildOnboardingContext(reduxState),
    [
      questionnaireConfig?.patientOnboarding,
      questionnaireConfig?.patientOffboarding,
      reduxState.consultation.practice?.slug,
      reduxState.consultation.practice?.patientDataCollectionConfig,
      reduxState.consultation.patient?.dateOfBirth,
      reduxState.consultation.patient,
      mode,
      reduxState.consultation.noAppointment,
      reduxState.searchParams,
      reduxState.graph?.variant,
      reduxState.graph?.experimentFlags,
      // Recompute the flow when the assisted-by-pro flag changes so the
      // in-office onboarding skip is applied as soon as the flag is set.
      assistedByPro,
    ],
  );

  useEffect(() => {
    const newFlow = computeFlow(onboardingContext);
    dispatch(setOnboardingFlow(newFlow));
  }, [onboardingContext, dispatch]);

  // const posthog = usePostHog();

  // useEffect(() => {
  //   if (mode === 'INVITATION') {
  //     const flagValue = posthog?.getFeatureFlag('patient-onboarding-mar-2026');
  //     const variantValue =
  //       typeof flagValue === 'string' ? flagValue : 'control';
  //     dispatch(
  //       setExperimentFlags({
  //         'patient-onboarding-mar-2026': variantValue,
  //       }),
  //     );
  //   } else {
  //     dispatch(setExperimentFlags({}));
  //   }
  // }, [mode, posthog, dispatch]);

  useEffect(() => {
    const stepId = routeToStepId(location.pathname);
    if (stepId) {
      const index = flow.indexOf(stepId);
      if (index !== -1 && index !== currentStepIndex) {
        dispatch(setCurrentStepIndex(index));
      }
    }
  }, [location.pathname, flow, currentStepIndex, dispatch]);

  const practice = useSelector(
    (state: RootState) => state.consultation?.practice,
  );

  useEffect(() => {
    // For an assisted launch, wait until the URL flag has been reflected into
    // Redux (and thus the flow recomputed) before redirecting — otherwise we'd
    // redirect to the stale flow[0] (landing-professional-select) computed
    // before the assisted skip was applied.
    if (assistedFromUrl && !assistedByPro) {
      return;
    }
    if (
      practice &&
      flow.length > 0 &&
      flow[0] !== 'landing' &&
      location.pathname === stepToRoute('landing' as StepId)
    ) {
      // For assisted launches, preserve the query string (`assisted=true`)
      // into flow[0] so the signal survives a late rehydrate after this
      // redirect. Other flows keep the original param-less behavior.
      navigate(
        stepToRoute(flow[0] as StepId) +
          (assistedFromUrl ? location.search : ''),
        { replace: true },
      );
    }
  }, [
    practice,
    flow,
    location.pathname,
    location.search,
    navigate,
    assistedFromUrl,
    assistedByPro,
  ]);

  const navigateToPage = useCallback(
    (page: string) => {
      navigate(`/questionnaire/${page.toLowerCase()}`);
    },
    [navigate],
  );

  const navigateToNextQuestionnairePage = useCallback(() => {
    const currentIndex = pages.indexOf(currentPage);
    if (currentIndex < pages.length - 1) {
      navigateToPage(pages[currentIndex + 1]);
    }
  }, [navigateToPage, currentPage, pages]);

  const navigateNext = useCallback(() => {
    isNavigatingRef.current = true;
    setNavigationDirection('forward');

    // Compute flow from the LATEST Redux state to ensure all transformers
    // are applied before navigation. Without this, the flow from the closure
    // may be stale (e.g., patient DOB was just updated but the useEffect
    // that recomputes the flow hasn't fired yet).
    const latestState = store.getState();
    const latestContext = buildOnboardingContext(latestState);
    const currentFlow = computeFlow(latestContext);
    const currentBaseFlow = getBaseFlow(latestContext);

    dispatch(setOnboardingFlow(currentFlow));

    const currentRoute = window.location.pathname;
    const currentStepId = routeToStepId(currentRoute);

    if (currentStepId) {
      let nextStepId: StepId | undefined;

      if (currentFlow.includes(currentStepId)) {
        const currentIdx = currentFlow.indexOf(currentStepId);
        if (currentIdx < currentFlow.length - 1) {
          nextStepId = currentFlow[currentIdx + 1] as StepId;
        }
      } else {
        // Step was removed by a transformer (e.g. auth step removed after
        // patient authenticates). Use previous flow to find the next surviving step.
        const posInPrev = flow.indexOf(currentStepId);
        if (posInPrev !== -1) {
          for (let i = posInPrev + 1; i < flow.length; i++) {
            if (currentFlow.includes(flow[i] as StepId)) {
              nextStepId = flow[i] as StepId;
              break;
            }
          }
        }
        if (!nextStepId) {
          const baseStepId = resolveBaseStepId(currentStepId);
          const posInBase = currentBaseFlow.indexOf(baseStepId);
          if (posInBase !== -1) {
            nextStepId = currentFlow.find((s) => {
              const baseS = resolveBaseStepId(s as StepId);
              return currentBaseFlow.indexOf(baseS) > posInBase;
            }) as StepId | undefined;
          }
        }
      }

      if (nextStepId) {
        if (
          isOffboardingStep(nextStepId) &&
          !isOffboardingStep(currentStepId)
        ) {
          if (pages.length > 0) {
            navigateToPage(pages[0]);
          }
        } else {
          const nextIdx = currentFlow.indexOf(nextStepId);
          dispatch(setCurrentStepIndex(nextIdx !== -1 ? nextIdx : 0));
          navigate(stepToRoute(nextStepId));
        }
      } else if (currentFlow.length > 0) {
        dispatch(setCurrentStepIndex(0));
        navigate(stepToRoute(currentFlow[0] as StepId));
      }
      return;
    }

    if (currentRoute.startsWith('/questionnaire/')) {
      const currentPageName = currentRoute
        .replace('/questionnaire/', '')
        .toUpperCase();
      const currentPageIndex = pages.findIndex((p) => p === currentPageName);

      if (currentPageIndex !== -1 && currentPageIndex < pages.length - 1) {
        navigateToNextQuestionnairePage();
      } else if (currentPageIndex === pages.length - 1) {
        // First offboarding step after the questionnaire's last page. This is
        // the additional-document-upload page when the professional enabled it
        // (it's inserted ahead of research-consent), otherwise research-consent
        // / confirmation / direct-confirmation.
        const firstOffboardingStep = currentFlow.find((s) =>
          isOffboardingStep(s as StepId),
        );
        if (firstOffboardingStep) {
          const offIdx = currentFlow.indexOf(firstOffboardingStep);
          dispatch(setCurrentStepIndex(offIdx));
          navigate(stepToRoute(firstOffboardingStep as StepId));
        }
      }
      return;
    }

    const pathSegments = currentRoute.split('/').filter(Boolean);
    if (
      pathSegments.length === 1 &&
      !currentRoute.startsWith('/questionnaire/') &&
      !currentRoute.startsWith('/intro/')
    ) {
      navigate(SHARED_ROUTES.LANDING_PAGE);
    }
  }, [
    store,
    pages,
    navigate,
    navigateToPage,
    dispatch,
    navigateToNextQuestionnairePage,
  ]);

  /**
   * Check if a page has displayable questions.
   *
   * This helper is used for BACKWARD NAVIGATION ONLY to skip empty pages.
   * Forward navigation empty-page skipping remains in Questionnaire.tsx because:
   * 1. Must wait for updateQuestions to process after landing on a page
   * 2. Questionnaire has access to loading states (isQueueReady, isComponentLoading, areQuestionsUpdating)
   * 3. Can only check if page is empty AFTER loading completes
   *
   * For backward navigation, we're returning to already-visited pages whose sections
   * are already populated in sectionQueues, so we can check emptiness before navigating.
   */
  const isPageEmpty = useCallback(
    (pageName: string) => {
      const state = store.getState();
      const config = state.questionnaire.config;
      const sectionQueues = state.questionnaire.sectionQueues;
      const questions = state.kb.questions;

      if (!config?.pages[pageName]) return true;

      // Calculate activeQueue for this page (same as useActiveQueue hook)
      const sections = config.pages[pageName].sections;
      const activeQueue = sections.flatMap(
        (section) => sectionQueues[section] || [],
      );

      // Filter out onboarding questions (same logic as Questionnaire)
      const displayQueue = filterOnboardingQuestions(activeQueue, pageName);

      // Empty if no questions, or only has one INFO question
      if (displayQueue.length === 0) return true;
      if (displayQueue.length === 1) {
        const question = questions[displayQueue[0]];
        return question?.format === 'INFO';
      }

      return false;
    },
    [store],
  );

  // Backward-navigation fallback when the questionnaire has no non-empty page
  // to land on: go to the last onboarding step of the flow.
  const navigateToLastOnboardingStep = useCallback(() => {
    const lastOnboardingIdx = flow.reduce(
      (last, s, i) => (!isOffboardingStep(s as StepId) ? i : last),
      -1,
    );
    if (lastOnboardingIdx >= 0) {
      dispatch(setCurrentStepIndex(lastOnboardingIdx));
      navigate(stepToRoute(flow[lastOnboardingIdx] as StepId));
    }
  }, [flow, dispatch, navigate]);

  const navigatePrevious = useCallback(() => {
    setNavigationDirection('backward');

    const currentRoute = location.pathname;
    const stepId = routeToStepId(currentRoute);

    if (stepId) {
      const index = flow.indexOf(stepId);
      if (index <= 0) return;

      const prevStepId = flow[index - 1] as StepId;

      if (isOffboardingStep(stepId) && !isOffboardingStep(prevStepId)) {
        // The last page may be empty : target the most recent non-empty page instead.
        const targetPage = [...pages]
          .reverse()
          .find((page) => !isPageEmpty(page));
        if (targetPage) {
          navigateToPage(targetPage);
          return;
        }
        // All pages empty: fall back to the last onboarding step.
        navigateToLastOnboardingStep();
        return;
      }

      dispatch(setCurrentStepIndex(index - 1));
      navigate(stepToRoute(prevStepId));
      return;
    }

    if (currentRoute.startsWith('/questionnaire/')) {
      const currentPageName = currentRoute
        .replace('/questionnaire/', '')
        .toUpperCase();
      const currentPageIndex = pages.findIndex((p) => p === currentPageName);

      if (currentPageIndex > 0) {
        // Find the most recent non-empty page before current
        const previousPages = pages.slice(0, currentPageIndex).reverse();
        const targetPage = previousPages.find((page) => !isPageEmpty(page));

        if (targetPage) {
          // Found a non-empty page, navigate to it
          // Note: navigateToPage dispatches changePage(targetPage)
          // The changePage reducer will detect backward navigation
          // and automatically set currentQuestionIndex to the LAST question on that page
          navigateToPage(targetPage);
          return;
        }
      }

      // No non-empty pages found (or at first page), go to last onboarding step
      navigateToLastOnboardingStep();
      return;
    }

    // Unsubscribe flow and other routes: go back in history
    if (currentRoute.includes('/unsub')) {
      navigate(-1);
      return;
    }
  }, [
    location.pathname,
    flow,
    pages,
    navigate,
    dispatch,
    navigateToPage,
    isPageEmpty,
    navigateToLastOnboardingStep,
  ]);

  const navigateToEditPage = useCallback(() => {
    navigate(`${SHARED_ROUTES.PATIENT_INFORMATION}?edit=true`);
  }, [navigate]);

  return (
    <QuestionnaireNavigationContext.Provider
      value={{
        navigateNext,
        navigatePrevious,
        navigateToEditPage,
        currentPage,
        showErrors,
        setShowErrors,
        isQuestionExiting,
        setIsQuestionExiting,
        hasFreshSelection,
        setHasFreshSelection,
        mandatoryQuestionsWarning,
        setMandatoryQuestionsWarning,
        navigationDirection,
        setNavigationDirection,
        isNavigatingRef,
      }}
    >
      {children}
    </QuestionnaireNavigationContext.Provider>
  );
};

export const useQuestionnaireNavigation = () =>
  useContext(QuestionnaireNavigationContext);
